import { useEffect, useState } from "preact/hooks";
import type { BookContact, ContactEmail, ContactPhone } from "../../prov/client";
import { t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";
import { Avatar, Switch } from "../widgets";

const fold = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
const PHONE_TYPES = ["work", "mobile", "home", "fax", "other"];
const EMAIL_TYPES = ["work", "home", "other"];

export function Contacts() {
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<S.Contact | "new" | null>(null);
  // « Créer un contact » from a card or after a call: the number filled in
  const [draft, setDraft] = useState<BookContact | undefined>(undefined);
  useEffect(() => {
    const d = S.contactDraft.value;
    if (!d) return;
    S.contactDraft.value = null;
    const [first, ...rest] = d.name.split(" ");
    setDraft({ first_name: first ?? "", last_name: rest.join(" "), phones: [{ type: "work", number: d.number }] } as BookContact);
    setEdit("new");
  }, [S.contactDraft.value]);
  const dir = S.directory.value;
  const f = fold(q.trim());
  const match = (c: { name: string; number: string; numbers?: string[]; company?: string }) =>
    !f || fold(c.name).includes(f) || fold(c.company ?? "").includes(f) || c.number.includes(f) || !!c.numbers?.some((n) => n.includes(f));
  const mine = S.contacts.value.filter(match);
  const company = dir.entries.filter(match);
  return (
    <div class="page">
      <div class="row between">
        <h1>{t("contacts.title")}</h1>
        <button type="button" class="btn soft" onClick={() => setEdit("new")}>
          <Icon name="plus" size={18} /> {t("contacts.new")}
        </button>
      </div>
      <input type="search" aria-label={t("contacts.search")} placeholder={t("contacts.search")} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
      {dir.name && <p class="muted" style={{ fontSize: "12px" }}>{t("contacts.includes", dir.name)}</p>}
      {edit && (
        <Editor
          key={edit === "new" ? `new:${draft?.phones?.[0]?.number ?? ""}` : edit.id}
          c={edit === "new" ? undefined : edit}
          prefill={edit === "new" ? draft : undefined}
          onDone={() => {
            setEdit(null);
            setDraft(undefined);
          }}
        />
      )}
      {mine.length > 0 && (
        <>
          <h2>{t("contacts.mine")}</h2>
          <div class="list">
            {mine.map((c) => (
              <Row c={c} onEdit={c.source === "shared" ? undefined : () => setEdit(c)} />
            ))}
          </div>
        </>
      )}
      {company.length > 0 && (
        <>
          <h2>{dir.name || t("contacts.directory")}</h2>
          <div class="list" data-testid="directory">
            {company.map((c) => (
              <Row c={{ id: `dir:${c.number}`, source: "directory", ...c }} />
            ))}
          </div>
        </>
      )}
      {!mine.length && !company.length && <p class="empty">{t("contacts.none")}</p>}
    </div>
  );
}

function Row({ c, onEdit }: { c: S.Contact; onEdit?: () => void }) {
  const sub = [c.number, c.company && c.company !== c.name ? c.company : "", c.source === "shared" ? c.bookName : ""].filter(Boolean).join(" · ");
  return (
    <div class="item">
      <Avatar name={c.name} />
      <div class="grow">
        <div class="title">
          {c.name} {c.favorite && <Icon name="star" size={14} />}
        </div>
        <div class="sub">{sub}</div>
      </div>
      {onEdit && (
        <button type="button" class="btn link" aria-label={t("contacts.edit")} title={t("contacts.edit")} onClick={onEdit}>
          <Icon name="edit" size={18} />
        </button>
      )}
      {S.chatEnabled.value && c.source === "directory" && c.number && (
        <button type="button" class="btn" aria-label={`${t("chat.write")} ${c.name}`} title={t("chat.write")} onClick={() => void S.openChatWith(c.number)}>
          <Icon name="chat" size={18} />
        </button>
      )}
      {c.number && (
        <button type="button" class="btn success" aria-label={`${t("dialer.call")} ${c.name}`} title={t("dialer.call")} onClick={() => void S.dial(c.number)}>
          <Icon name="phone" size={18} />
        </button>
      )}
    </div>
  );
}

/* Numbers and emails keep their id (a new one has none), so changing a
 * number is an update of that number on the server, not a remove and an add. */
type Phone = Partial<ContactPhone>;
type Email = Partial<ContactEmail>;

interface Draft {
  first_name: string;
  last_name: string;
  company: string;
  title: string;
  note: string;
  favorite: boolean;
  phones: Phone[];
  emails: Email[];
}

function draftOf(raw?: BookContact): Draft {
  const phones = (raw?.phones ?? []).map((p) => ({ ...p }));
  return {
    first_name: raw?.first_name ?? "",
    last_name: raw?.last_name ?? "",
    company: raw?.company ?? "",
    title: raw?.title ?? "",
    note: raw?.note ?? "",
    favorite: raw?.favorite === true,
    phones: phones.length ? phones : [{ type: "mobile", number: "" }],
    emails: (raw?.emails ?? []).map((e) => ({ ...e })),
  };
}

const listStyle = { display: "grid", gap: "6px", marginBottom: "12px" };
const labelStyle = { color: "var(--textSecondary)", fontSize: "13px" };

function Editor({ c, prefill, onDone }: { c?: S.Contact; prefill?: BookContact; onDone: () => void }) {
  const raw = c?.raw as BookContact | undefined;
  const [v, setV] = useState(() => draftOf(raw ?? prefill));
  const text = (k: "first_name" | "last_name" | "company" | "title" | "note") => (e: Event) =>
    setV({ ...v, [k]: (e.target as HTMLInputElement).value });
  const setPhone = (i: number, p: Phone) => setV({ ...v, phones: v.phones.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const setEmail = (i: number, m: Email) => setV({ ...v, emails: v.emails.map((x, j) => (j === i ? { ...x, ...m } : x)) });
  const out = {
    first_name: v.first_name.trim(),
    last_name: v.last_name.trim(),
    company: v.company.trim(),
    title: v.title.trim(),
    note: v.note.trim(),
    favorite: v.favorite,
    phones: v.phones.map((p) => ({ ...p, number: (p.number ?? "").trim() })).filter((p) => p.number),
    emails: v.emails.map((m) => ({ ...m, address: (m.address ?? "").trim() })).filter((m) => m.address),
  };
  // the core refuses a contact with nothing to show
  const empty = !out.first_name && !out.last_name && !out.company && !out.phones.length && !out.emails.length;
  return (
    <form
      class="card"
      style={{ margin: "16px 0" }}
      onSubmit={(e) => {
        e.preventDefault();
        // the whole contact: what this page doesn't show (addresses, tags, ext...) stays
        void S.saveContact({ ...raw, id: c?.id, ...out }).then((ok) => ok && onDone());
      }}
    >
      <h2 style={{ marginTop: 0 }}>{c ? t("contacts.edit") : t("contacts.new")}</h2>
      <div class="row" style={{ alignItems: "start" }}>
        <label class="field" style={{ flex: 1 }}>
          <span>{t("contacts.first_name")}</span>
          <input type="text" value={v.first_name} autoFocus onInput={text("first_name")} />
        </label>
        <label class="field" style={{ flex: 1 }}>
          <span>{t("contacts.last_name")}</span>
          <input type="text" value={v.last_name} onInput={text("last_name")} />
        </label>
      </div>
      <div class="row" style={{ alignItems: "start" }}>
        <label class="field" style={{ flex: 1 }}>
          <span>{t("contacts.company")}</span>
          <input type="text" value={v.company} onInput={text("company")} />
        </label>
        <label class="field" style={{ flex: 1 }}>
          <span>{t("contacts.job_title")}</span>
          <input type="text" value={v.title} onInput={text("title")} />
        </label>
      </div>

      <div style={listStyle}>
        <span style={labelStyle}>{t("contacts.numbers")}</span>
        {v.phones.map((p, i) => (
          <div class="row" style={{ gap: "8px" }}>
            <select
              aria-label={t("contacts.number_type")}
              value={p.type ?? "work"}
              style={{ maxWidth: "140px" }}
              onChange={(e) => setPhone(i, { type: (e.target as HTMLSelectElement).value })}
            >
              {PHONE_TYPES.map((ty) => (
                <option value={ty}>{t(`contacts.type.${ty}`)}</option>
              ))}
            </select>
            <input type="tel" aria-label={t("contacts.number")} value={p.number ?? ""} onInput={(e) => setPhone(i, { number: (e.target as HTMLInputElement).value })} />
            <button
              type="button"
              class="btn link"
              aria-label={t("contacts.remove_number")}
              title={t("contacts.remove_number")}
              onClick={() => setV({ ...v, phones: v.phones.filter((_, j) => j !== i) })}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        ))}
        <div>
          <button type="button" class="btn link" onClick={() => setV({ ...v, phones: [...v.phones, { type: "mobile", number: "" }] })}>
            <Icon name="plus" size={16} /> {t("contacts.add_number")}
          </button>
        </div>
      </div>

      <div style={listStyle}>
        <span style={labelStyle}>{t("contacts.emails")}</span>
        {v.emails.map((m, i) => (
          <div class="row" style={{ gap: "8px" }}>
            <select
              aria-label={t("contacts.email_type")}
              value={m.type ?? "work"}
              style={{ maxWidth: "140px" }}
              onChange={(e) => setEmail(i, { type: (e.target as HTMLSelectElement).value })}
            >
              {EMAIL_TYPES.map((ty) => (
                <option value={ty}>{t(`contacts.type.${ty}`)}</option>
              ))}
            </select>
            <input type="email" aria-label={t("contacts.email")} value={m.address ?? ""} onInput={(e) => setEmail(i, { address: (e.target as HTMLInputElement).value })} />
            <button
              type="button"
              class="btn link"
              aria-label={t("contacts.remove_email")}
              title={t("contacts.remove_email")}
              onClick={() => setV({ ...v, emails: v.emails.filter((_, j) => j !== i) })}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        ))}
        <div>
          <button type="button" class="btn link" onClick={() => setV({ ...v, emails: [...v.emails, { type: "work", address: "" }] })}>
            <Icon name="plus" size={16} /> {t("contacts.add_email")}
          </button>
        </div>
      </div>

      <label class="field">
        <span>{t("contacts.note")}</span>
        <textarea rows={2} value={v.note} onInput={text("note")} />
      </label>
      <div class="row between" style={{ marginBottom: "12px" }}>
        <span>{t("contacts.favorite")}</span>
        <Switch label={t("contacts.favorite")} checked={v.favorite} onChange={(f) => setV({ ...v, favorite: f })} />
      </div>

      <div class="row between">
        {c ? (
          <button type="button" class="btn danger" onClick={() => void S.deleteContact(c.id).then(onDone)}>
            <Icon name="trash" size={16} /> {t("common.delete")}
          </button>
        ) : (
          <span />
        )}
        <div class="row">
          <button type="button" class="btn" onClick={onDone}>
            {t("common.cancel")}
          </button>
          <button type="submit" class="btn primary" disabled={empty}>
            {t("common.save")}
          </button>
        </div>
      </div>
    </form>
  );
}
